/**
 * The sign-in dialog and the MFA step that can follow it.
 *
 * Moved out of the desktop `Layout` (2026-09-28) so the phone shell has it too:
 * it is opened from anywhere through `LoginDialogContext`, and while it was
 * drawn only by `Layout`, every "sign in" on a phone — including a shared
 * report's *Sign in to view* — opened nothing. Mounted once, by
 * `ResponsiveLayout`, whichever shell is showing.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../firebase';
import { ACCOUNT_EXISTS_MESSAGE } from '../../auth/signInMessages';
import { useLoginDialog } from '../../contexts/LoginDialogContext';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import analyticsService, { ANALYTICS_SOURCES } from '../../services/analytics';
import TotpVerification from '../TotpVerification';
import TrustLine from './TrustLine';

const SignInDialog: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { effectiveTheme } = useAppTheme();
  const { signInWithGoogle, signInWithMicrosoft, signInWithTwitter, mfaResolver, mfaPending, clearMfaState, onMfaSuccess, error: authError } = useAuth();
  const { isOpen: loginDialogOpen, closeLoginDialog } = useLoginDialog();
  // The reason a sign-in did not finish ([[BUGS #429]]). AuthContext always set
  // it and nothing ever showed it — an email already registered under another
  // method ended in a dialog that closed and a reader who was not signed in.
  // Shown only after a click in THIS opening, so a stale one never greets a
  // reader who comes back.
  const [attempted, setAttempted] = useState(false);
  useEffect(() => { if (loginDialogOpen) setAttempted(false); }, [loginDialogOpen]);

  // Grayscale only (no blue) — as it was in Layout.
  const getHeadingColor = () =>
    effectiveTheme === 'light' ? theme.palette.grey[900] : 'rgba(255, 255, 255, 0.87)';

  return (
    <>
      {/* Login Dialog */}
      <Dialog
        open={loginDialogOpen}
        onClose={closeLoginDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary'
          }
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            color: 'text.primary'
          }}
        >
          <Typography component="h2" variant="h5" fontWeight={700} sx={{ color: getHeadingColor() }}>
            Sign in to IndexAlpha
          </Typography>
          <IconButton onClick={closeLoginDialog} size="small" sx={{ color: 'text.primary' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Typography variant="body1" color="text.primary" mb={3}>
            Personalise your AI, save portfolios, and track your watchlist
          </Typography>
          
          {attempted && authError && (
            <Typography
              role="alert"
              variant="body2"
              sx={{
                // A returning reader is not an error — only real failures are red.
                color: authError === ACCOUNT_EXISTS_MESSAGE ? 'text.primary' : 'error.main',
                fontWeight: authError === ACCOUNT_EXISTS_MESSAGE ? 500 : 400,
                mb: 2,
              }}
            >
              {authError}
            </Typography>
          )}

          <Stack spacing={2}>
            {/* Google Login - Enabled */}
            <Button
              variant="outlined"
              fullWidth
              size="large"
              startIcon={
                <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center' }}>
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                    <path fill="none" d="M1 1h22v22H1z" />
                  </svg>
                </Box>
              }
              onClick={async () => {
                const source = ANALYTICS_SOURCES.NAVBAR;
                analyticsService.trackSignIn('started', 'google', source);
                setAttempted(true);
                try {
                  const outcome = await signInWithGoogle();
                  // Only a finished sign-in counts. A redirect is recorded when
                  // it returns (AuthContext boot handler); an 'incomplete' one
                  // keeps the dialog open for the second factor or the error.
                  if (outcome === 'signed-in') {
                    analyticsService.trackSignIn('success', 'google', source);
                    closeLoginDialog();
                  }
                } catch (error: any) {
                  console.error('Google sign-in error:', error);
                  analyticsService.trackSignIn('error', 'google', source, error?.toString());
                }
              }}
              sx={{
                borderColor: 'divider',
                color: 'text.primary',
                textTransform: 'none',
                py: 1.5,
                fontWeight: 500,
                fontSize: '0.875rem',
                '&:hover': {
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  boxShadow: '0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15)'
                }
              }}
            >
              Sign in with Google
            </Button>

            {/* Microsoft Login Button */}
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={async () => {
                const source = ANALYTICS_SOURCES.NAVBAR;
                analyticsService.trackSignIn('started', 'microsoft', source);
                setAttempted(true);
                try {
                  await signInWithMicrosoft();
                  // The handler RETURNS (it does not throw) when the email is
                  // already registered or a second factor is needed — so only
                  // a signed-in user closes the dialog ([[BUGS #429]]).
                  if (auth.currentUser) {
                    analyticsService.trackSignIn('success', 'microsoft', source);
                    closeLoginDialog();
                  }
                } catch (error: any) {
                  console.error('Microsoft sign-in error:', error);
                  analyticsService.trackSignIn('error', 'microsoft', source, error?.toString());
                  // Error is already handled in AuthContext
                }
              }}
              startIcon={
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" 
                  alt="Microsoft" 
                  style={{ width: 20, height: 20 }}
                />
              }
              sx={{
                borderColor: 'divider',
                color: 'text.primary',
                textTransform: 'none',
                py: 1.5,
                fontWeight: 500,
                fontSize: '0.875rem',
                '&:hover': {
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  boxShadow: '0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15)'
                }
              }}
            >
              Sign in with Microsoft
            </Button>

            {/* X (Twitter) Login Button */}
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={async () => {
                const source = ANALYTICS_SOURCES.NAVBAR;
                analyticsService.trackSignIn('started', 'twitter', source);
                setAttempted(true);
                try {
                  await signInWithTwitter();
                  // The handler RETURNS (it does not throw) when the email is
                  // already registered or a second factor is needed — so only
                  // a signed-in user closes the dialog ([[BUGS #429]]).
                  if (auth.currentUser) {
                    analyticsService.trackSignIn('success', 'twitter', source);
                    closeLoginDialog();
                  }
                } catch (error: any) {
                  console.error('X sign-in error:', error);
                  analyticsService.trackSignIn('error', 'twitter', source, error?.toString());
                  // Error is already handled in AuthContext
                }
              }}
              startIcon={
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/5/57/X_logo_2023_%28white%29.png" 
                  alt="X" 
                  style={{ width: 18, height: 18, filter: 'invert(1)' }}
                />
              }
              sx={{
                borderColor: 'divider',
                color: 'text.primary',
                textTransform: 'none',
                py: 1.5,
                fontWeight: 500,
                fontSize: '0.875rem',
                '&:hover': {
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  boxShadow: '0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15)'
                }
              }}
            >
              Sign in with X
            </Button>

          </Stack>

          <TrustLine />

          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              textAlign: 'center', 
              mt: 2,
              color: 'text.primary' 
            }}
          >
            By continuing, you agree to our{' '}
            <Box
              component="span"
              sx={{
                color: 'grey.700',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={() => navigate('/terms')}
            >
              Terms of Service
            </Box>
            {' '}and{' '}
            <Box
              component="span"
              sx={{
                color: 'grey.700',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={() => navigate('/privacy')}
            >
              Privacy Policy
            </Box>
          </Typography>
        </DialogContent>
      </Dialog>

      {/* TOTP MFA Verification Dialog */}
      <TotpVerification
        open={mfaPending}
        resolver={mfaResolver}
        onSuccess={() => {
          onMfaSuccess();
          closeLoginDialog();
        }}
        onCancel={clearMfaState}
      />

    </>
  );
};

export default SignInDialog;
